/**
 * Ultrasound Baseline Recommendation Hook
 * 
 * Custom hook for managing ultrasound baseline recommendation data including loading,
 * error handling, and automatic data fetching on parameter changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { UltsBaselineRecommendation, UltrasoundError } from '../services/equipment/types';
import { ultrasoundService } from '../services/equipment/ultrasound-service';

interface UseUltsBaselineRecommendationOptions {
  /** Site identifier */
  site?: string;
  /** Asset identifier */
  asset?: string;
  /** Position identifier (may include equipment prefix) */
  position?: string;
}

interface UseUltsBaselineRecommendationReturn {
  /** Recommendation data */
  data: UltsBaselineRecommendation | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Ultrasound-specific error object for detailed error handling */
  ultrasoundError: UltrasoundError | null;
  /** Fetch recommendation data function */
  fetchRecommendation: (site: string, asset: string, position: string) => Promise<void>;
  /** Retry the last failed request */
  retry: () => Promise<void>;
  /** Clear data and reset state */
  clear: () => void;
}

/**
 * Hook for managing ultrasound baseline recommendation data
 */
export const useUltsBaselineRecommendation = (
  options: UseUltsBaselineRecommendationOptions
): UseUltsBaselineRecommendationReturn => {
  const {
    site,
    asset,
    position
  } = options;

  const [data, setData] = useState<UltsBaselineRecommendation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ultrasoundError, setUltrasoundError] = useState<UltrasoundError | null>(null);
  const [currentSite, setCurrentSite] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<string | null>(null);

  /**
   * Fetch ultrasound baseline recommendation data
   */
  const fetchRecommendation = useCallback(async (
    targetSite: string,
    targetAsset: string,
    targetPosition: string
  ) => {
    if (!targetSite || !targetAsset || !targetPosition) return;

    setIsLoading(true);
    setError(null);
    setUltrasoundError(null);

    try {
      const recommendationData = await ultrasoundService.getBaselineRecommendation(
        targetSite,
        targetAsset,
        targetPosition
      );
      
      setData(recommendationData);
      setCurrentSite(targetSite);
      setCurrentAsset(targetAsset);
      setCurrentPosition(targetPosition);
    } catch (err) {
      if (err instanceof UltrasoundError) {
        setUltrasoundError(err);
        setError(err.message);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ultrasound recommendation';
        setError(errorMessage);
        setUltrasoundError(null);
      }
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry the last failed request
   */
  const retry = useCallback(async () => {
    if (!currentSite || !currentAsset || !currentPosition) return;
    await fetchRecommendation(currentSite, currentAsset, currentPosition);
  }, [currentSite, currentAsset, currentPosition, fetchRecommendation]);

  /**
   * Clear data and reset state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setUltrasoundError(null);
    setCurrentSite(null);
    setCurrentAsset(null);
    setCurrentPosition(null);
  }, []);

  // Auto-fetch when parameters change - always fetch when valid parameters are provided
  useEffect(() => {
    // Always fetch data when all parameters are provided and any parameter has changed
    if (site && asset && position) {
      if (site !== currentSite || asset !== currentAsset || position !== currentPosition) {
        fetchRecommendation(site, asset, position);
      }
    }
  }, [site, asset, position, currentSite, currentAsset, currentPosition, fetchRecommendation]);

  // Clear data when parameters are removed
  useEffect(() => {
    if (
      (!site && currentSite) || 
      (!asset && currentAsset) || 
      (!position && currentPosition)
    ) {
      clear();
    }
  }, [site, currentSite, asset, currentAsset, position, currentPosition, clear]);

  return {
    data,
    isLoading,
    error,
    ultrasoundError,
    fetchRecommendation,
    retry,
    clear
  };
};